import React, { useState, useEffect, useRef } from "react";
import { Stage, Layer } from "react-konva";
import { jsPDF } from "jspdf";
import { Grid, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import {
  formatDate,
  getAttributeValue,
  getAttribute,
  getEntityAddress,
} from "../../../../../../Utilities/Functions";
import { recordTypes } from "../../../../../../API/config";
import Record from "../../Component/Record";
import Connector from "../../Component/Connector";
import Toolbar from "../../Component/Toolbar";

const Canvas = (props) => {
  const {
    entity,
    setSelectedRecord,
    setSelectedRecordChilds,
    reloadSelected,
    reloadSelectedChilds,
    getParents,
    showRecordMenu,
    hideRecordMenu,
    showRecordInfo,
    hideRecordInfo,
    showPercentageDialog,
    hidePercentageDialog,
    onReload,
    hasRecordPermission,
  } = props;

  const recordShapeWidth = 200;
  const recordShapeHeight = 100;
  const recordYGap = 64;
  const recordXGap = 50;
  const maxRecsPerRow = 4;

  const [stageScale, setStageScale] = useState(1);
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });

  const [canvasWidth, setCanvasWidth] = useState(recordShapeWidth + recordXGap);
  const [canvasHeight, setCanvasHeight] = useState(
    recordShapeWidth + recordYGap,
  );

  const [orgStructure, setOrgStructure] = useState({});
  const [records, setRecords] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState();

  const [hasAml, setHasAml] = useState();
  const [canCreateAml, setCanCreateAml] = useState();

  const stageRef = useRef();

  const { t } = useTranslation();

  useEffect(() => {
    const checkAml = () => {
      const { checkRequests = [], permissions } = entity;
      const amlCr = checkRequests.find((cr) => cr.check_type === "AML");
      const { process_flow_state } = amlCr || {};
      setHasAml(process_flow_state && process_flow_state !== "Incomplete");
      const canCreateAml = hasRecordPermission(
        permissions,
        recordTypes.checkRequest.actions.createAmlCheck.name,
      );
      setCanCreateAml(canCreateAml);
    };

    const setOrgStruc = async () => {
      const {
        id,
        urn,
        attributes,
        scores = [],
        addresses,
        permissions,
      } = entity || {};
      setOrgStructure({
        id,
        urn,
        attributes,
        scores,
        permissions,
        address: getEntityAddress(addresses),
        parents: [],
        showParents: false,
        autoloadParents: true,
      });
    };
    if (entity) {
      setOrgStruc();
      checkAml();
    }
  }, [entity]);

  useEffect(() => {
    let canvasWidth = window.innerWidth - 220;
    let canvasHeight = window.innerHeight - 250;

    const records = [];
    const connectors = [];

    const getPermissions = (permissions) => {
      const canUpdateRecord = hasRecordPermission(
        permissions,
        recordTypes.record.actions.update.name,
      );
      let canAddAssociation = false;
      let canAddDocument = false;
      let canUpdateAssociation = false;
      let canRemoveAssociation = false;
      if (canUpdateRecord) {
        canAddAssociation = hasRecordPermission(
          permissions,
          recordTypes.record.actions.addAssociation.name,
        );
        canUpdateAssociation = hasRecordPermission(
          permissions,
          recordTypes.record.actions.updateAssociation.name,
        );
        canAddDocument = hasRecordPermission(
          permissions,
          recordTypes.record.actions.addAmlDocument.name,
        );
        canRemoveAssociation = hasRecordPermission(
          permissions,
          recordTypes.record.actions.removeAssociation.name,
        );
      }

      return {
        canAddAssociation,
        canUpdateAssociation,
        canRemoveAssociation,
        canAddDocument,
      };
    };

    const convertAddress = (address = {}) => {
      return {
        addressLine1: address.address_line_1,
        addressLine2: address.address_line_2,
        addressType: address.addressType,
        city: address.city,
        countryIso3: address.countryIso3,
        postalCode: address.postalCode,
        stateProvince: address.stateProvince,
      };
    };

    const convertRecord = (record, level) => {
      const { id, urn, attributes, scores = [], address, permissions } = record;

      const recordName = getAttributeValue(getAttribute(attributes, "3"));
      const mdmId = getAttributeValue(getAttribute(attributes, "1"));
      const dunsNumber = getAttributeValue(getAttribute(attributes, "2"));
      const entityCategory = getAttributeValue(getAttribute(attributes, "8"));
      const entitySubcategory = getAttributeValue(
        getAttribute(attributes, "72"),
      );
      const countryOfOperations = getAttributeValue(
        getAttribute(attributes, "35"),
      );
      const kycScore = scores.find((s) => s.check_type === "KYC") || {};
      const amlScore = scores.find((s) => s.check_type === "AML") || {};
      const hasParents =
        record.showParents && (record.parents || []).length > 0;
      return {
        id,
        urn,
        level,
        recordName,
        mdmId,
        dunsNumber,
        entityCategory,
        entitySubcategory,
        countryOfOperations,
        address: convertAddress(address),
        permissions: getPermissions(permissions),
        hasParents,
        width: recordShapeWidth,
        height: recordShapeHeight,
        kycScore: kycScore.accepted_score || kycScore.guided_score || "-",
        kycScoreValidTo: formatDate(kycScore.expiry_date) || "-",
        amlScore: amlScore.accepted_score || amlScore.guided_score || "-",
        amlScoreValidTo: formatDate(amlScore.expiry_date) || "-",
        kycExpired: kycScore.expired,
        amlExpired: amlScore.expired
      };
    };

    const getPercentage = (attributeValues) => {
      const pa = attributeValues.find((a) => a.attribute_id === "81");
      return getAttributeValue(pa);
    };

    const processRecords = (cr, level = 1, parent) => {
      if (!cr || !cr.id) {
        return;
      }
      let r = records.find((r) => r.id === cr.id);
      if (!r) {
        r = convertRecord(cr, level);
        records.push(r);
        if (cr.showParents && cr.parents && cr.parents.length > 0) {
          cr.parents.forEach((cpr, idx) => {
            processRecords(cpr, level + 1, r);
          });
        }
      }
      r.level = Math.max(r.level, level);
      r.showParents = cr.showParents;
      if (parent) {
        const connector = connectors.find(
          (c) => c.sourceRec.id === parent.id && c.targetRec.id === r.id,
        );
        if (!connector) {
          const value = getPercentage(cr.associationAttributeValues);
          const label = value ? `${value}%` : "-";
          connectors.push({ sourceRec: parent, targetRec: r, label, value });
        }
      }
    };

    const setRecordPositions = () => {
      const cw =
        maxRecsPerRow * recordShapeWidth + (maxRecsPerRow + 1) * recordXGap;
      if (cw > canvasWidth) {
        canvasWidth = cw;
      }

      let maxLevels = 1;
      for (let r of records) {
        let level = r.level;
        const connectedFrom = connectors.filter((c) => c.targetRec.id === r.id);
        connectedFrom.forEach((c) => {
          if (c.sourceRec.level > level) {
            level = c.sourceRec.level;
          }
        });
        r.level = r.id === entity.id ? 1 : level;
        if (level > maxLevels) {
          maxLevels = level;
        }
      }

      let totalRows = 0;
      for (let level = 1; level <= maxLevels; level++) {
        let recs = records.filter((r) => r.level === level);
        if (recs.length > 0) {
          recs.forEach((r, idx) => {
            r.row = totalRows + Math.ceil((idx + 1) / maxRecsPerRow);
          });
          totalRows += Math.ceil(recs.length / maxRecsPerRow);
          recs = records.filter((r) => r.level === level);
        }
      }

      const ch = totalRows * (recordShapeHeight + recordYGap) + recordYGap;
      if (ch > canvasHeight) {
        canvasHeight = ch;
      }

      let row = 1;
      let rowrecs = records.filter((r) => r.row === row);
      while (rowrecs.length > 0) {
        const tw =
          rowrecs.length * recordShapeWidth + (rowrecs.length - 1) * recordXGap;
        const middleX = (canvasWidth - tw) / 2;

        const xGroups = [];
        rowrecs.forEach((r, idx) => {
          let x;
          const connectedFrom = connectors.filter(
            (c) => c.targetRec.id === r.id,
          );
          if (connectedFrom.length > 0) {
            const xs = connectedFrom.map((c) =>
              c.sourceRec.x ? c.sourceRec.x : recordXGap,
            );
            const sum = xs.reduce((a, b) => a + b, 0);
            x = sum / xs.length;
          } else {
            x = middleX;
          }

          const xGrp = xGroups.find((g) => g.x === x);
          if (!xGrp) {
            xGroups.push({ x, recs: [r] });
          } else {
            xGrp.recs.push(r);
          }
          r.y = canvasHeight - (recordShapeHeight + recordYGap) * r.row;
        });
        xGroups.sort((g1, g2) => {
          return g1.x > g2.x ? 1 : g1.x < g2.x ? -1 : 0;
        });

        let prevX = 0;
        xGroups.forEach((grp, idx) => {
          const recs = grp.recs;
          const tw =
            recs.length * recordShapeWidth + (recs.length - 1) * recordXGap;
          let x = grp.x + recordShapeWidth / 2 - tw / 2;
          x = x - (x % recordXGap);
          if (x < prevX + recordXGap) {
            x = prevX + recordXGap;
          }
          recs.forEach((rec, idx) => {
            rec.x = x + idx * (recordShapeWidth + recordXGap);
          });
          prevX = x + tw;
        });

        row++;
        rowrecs = records.filter((r) => r.row === row);
      }

      records.forEach((r) => {
        const connectedFrom = connectors.filter((c) => c.targetRec.id === r.id);
        if (connectedFrom.length > 0) {
          connectedFrom.sort((c1, c2) =>
            c1.sourceRec.x < c2.sourceRec.x
              ? -1
              : c1.sourceRec.x > c2.sourceRec.x
              ? 1
              : 0,
          );
          r.connectorPoints = {};
          const distance = r.width / (connectedFrom.length + 1);
          connectedFrom.forEach((c, idx) => {
            r.connectorPoints[c.sourceRec.id] = {
              x: r.x + (idx + 1) * distance,
              y: r.y + r.height,
            };
          });
        }
      });

      records.sort((r1, r2) => {
        if (r1.y === r2.y) {
          return r1.x < r2.x ? -1 : 1;
        }
        return r1.y > r2.y ? -1 : 1;
      });
    };

    const setMenuVisibility = () => {
      records.forEach((r) => {
        const childs = records.filter(
          (cr) => Object.keys(r.connectorPoints || {}).indexOf(cr.id) > -1,
        );
        const wp = childs.filter((cr) => cr.permissions.canRemoveAssociation);
        r.showMenu =
          r.permissions.canAddAssociation ||
          r.permissions.canAddDocument ||
          wp.length > 0;
      });
    };

    const getNonIntersactingX = (sourceRec, targetRec, x, dir) => {
      let intersects = false;
      for (let rec of records) {
        if (
          rec.id !== targetRec.id &&
          rec.id !== sourceRec.id &&
          ((rec.y > targetRec.y && rec.y < sourceRec.y) ||
            (rec.y > sourceRec.y && rec.y < targetRec.y))
        ) {
          if (x >= rec.x && x <= rec.x + rec.width) {
            intersects = true;
            break;
          }
        }
      }
      if (intersects) {
        return getNonIntersactingX(sourceRec, targetRec, x + dir * 15, dir);
      } else {
        return x;
      }
    };

    const findNonOverlappingY = (p1, p2, yLines, yLimit) => {
      const x1 = p1.x < p2.x ? p1.x : p2.x;
      const x2 = p1.x < p2.x ? p2.x : p1.x;
      if (!yLimit) {
        yLimit = p1.y - 24;
      }
      if (!yLines[p2.y]) {
        yLines[p2.y] = [{ x1, x2 }];
        return p2.y;
      } else {
        let overlaps = false;
        for (let p of yLines[p2.y]) {
          if (!(p.x2 <= x1 || p.x1 >= x2)) {
            overlaps = true;
            break;
          }
        }
        if (overlaps) {
          p1.y = p2.y = p2.y - 3;
          if (p1.y < yLimit) {
            p1.y = p2.y = yLimit;
            return yLimit;
          }
          const y = findNonOverlappingY(p1, p2, yLines, yLimit);
          if (yLines[y]) {
            yLines[y].push({ x1, x2 });
          } else {
            yLines[y] = [{ x1, x2 }];
          }
        } else {
          yLines[p2.y].push({ x1, x2 });
          return p2.y;
        }
      }
    };

    const findNonOverlappingX = (p1, p2, xLines, dir, xLimit) => {
      const y1 = p1.y < p2.y ? p1.y : p2.y;
      const y2 = p1.y < p2.y ? p2.y : p1.y;

      if (!xLimit) {
        xLimit = p1.x + dir * 24;
      }
      if (!xLines[p2.x]) {
        xLines[p2.x] = [{ y1, y2 }];
        return p2.x;
      } else {
        let overlaps = false;
        for (let p of xLines[p2.x]) {
          if (!(p.y2 <= y1 || p.y1 >= y2)) {
            overlaps = true;
            break;
          }
        }
        if (overlaps) {
          p1.x = p2.x = p2.x - dir * 3;
          if ((dir < 0 && p1.x < xLimit) || (dir > 0 && p1.x > xLimit)) {
            p1.x = p2.x = xLimit;
            return xLimit;
          }
          const x = findNonOverlappingX(p1, p2, xLines, dir, xLimit);
          if (xLines[x]) {
            xLines[x].push({ y1, y2 });
          } else {
            xLines[x] = [{ y1, y2 }];
          }
        } else {
          xLines[p2.x].push({ y1, y2 });
          return p2.x;
        }
      }
    };

    const setConnectorPositions = () => {
      const yLines = {};
      const xLines = {};
      records.forEach((r) => {
        const rcs = connectors.filter((c) => c.sourceRec.id === r.id);
        rcs.sort((c1, c2) => {
          if (c1.targetRec.y === c2.targetRec.y) {
            if (c1.targetRec.x < c2.sourceRec.x) {
              return c1.targetRec.x < c2.targetRec.x ? -1 : 1;
            } else {
              return c1.targetRec.x < c2.targetRec.x ? 1 : -1;
            }
          }

          return c1.targetRec.y > c2.targetRec.y ? -1 : 1;
        });

        rcs.forEach((c) => {
          const { sourceRec, targetRec } = c;
          let p1, p2, p3, p4, p5, p6;
          let x = sourceRec.x + sourceRec.width / 2;
          let y = sourceRec.y - 13;
          p1 = { x, y };
          y = sourceRec.y - 20;
          p2 = { x, y };

          const dir = sourceRec.x <= targetRec.x ? 1 : -1;
          if (sourceRec.y === targetRec.y) {
            x = dir > 0 ? targetRec.x - 15 : targetRec.x + targetRec.width + 15;
          } else if (targetRec.y > sourceRec.y) {
            x = x + dir * (sourceRec.width / 2 + 15);
          }
          if (
            Math.abs(targetRec.y + targetRec.height - sourceRec.y) !==
            recordYGap
          ) {
            x = getNonIntersactingX(sourceRec, targetRec, x, dir);
            p3 = { x, y };
            y = targetRec.y + targetRec.height + (recordYGap - 20);
            p4 = { x, y };
          } else if (p2.x !== x) {
            p3 = { x, y };
          }

          let cp = targetRec.connectorPoints[sourceRec.id];
          if (!cp) {
            cp = {
              x: targetRec.x + targetRec.width / 2,
              y: targetRec.y + targetRec.height,
            };
          }
          x = cp.x;
          p5 = { x, y };
          y = cp.y;
          p6 = { x, y };

          if (p2 && p3 && p2.y === p3.y && p2.x != p3.x) {
            findNonOverlappingY(p2, p3, yLines);
          } else if (p2 && p5 && p2.y === p5.y && p2.x != p5.x) {
            findNonOverlappingY(p2, p5, yLines);
          }
          if (p4 && p5 && p4.y === p5.y && p4.x != p5.x) {
            findNonOverlappingY(p4, p5, yLines);
          }
          if (p3 && p4 && p3.x === p4.x && p3.y != p4.y) {
            const dir = p2.x < p3.x ? -1 : 1;
            findNonOverlappingX(p3, p4, xLines, dir);
          }

          c.points = [p1, p2];
          if (p3) {
            c.points.push(p3);
          }
          if (p4) {
            c.points.push(p4);
          }
          c.points.push(p5, p6);
        });
      });
    };

    if (!orgStructure) {
      return;
    }
    if (orgStructure.showParents === undefined) {
      orgStructure.showParents = true;
    }
    processRecords(orgStructure);
    setRecordPositions();
    setConnectorPositions();
    setMenuVisibility();
    if (records) {
      setRecords(records);
      setConnectors(connectors);
    }
    setCanvasWidth(canvasWidth);
    setCanvasHeight(canvasHeight);
    if (!selectedRecordId) {
      setSelectedRecordId(orgStructure.id);
    }
  }, [orgStructure]);

  useEffect(() => {
    const reloadAllChilds = async () => {
      const r = records.find((r) => r.id === selectedRecordId);
      const childs = records.filter(
        (cr) => Object.keys(r.connectorPoints || {}).indexOf(cr.id) > -1,
      );
      childs.forEach((c) => reload(c.id));
      onReload();
    };

    const reload = async (id) => {
      const recs = findInOrgStructure(orgStructure, id);
      const r = records.find((r) => r.id === id);
      r.isLoading = true;
      const parents = await getParents(id);
      if (recs.length > 0) {
        recs.forEach((r) => {
          r.parents = parents;
          r.showParents = true;
        });
        setOrgStructure({ ...orgStructure });
      } else {
        const {
          id,
          urn,
          attributes,
          scores = [],
          addresses,
          permissions,
        } = entity || {};
        setOrgStructure({
          id,
          urn,
          attributes,
          scores,
          permissions,
          address: getEntityAddress(addresses),
          parents: parents,
          showParents: true,
          autoloadParents: true,
        });
      }
      r.isLoading = false;
      onReload();
    };

    if (reloadSelected) {
      reload(selectedRecordId);
    }
    if (reloadSelectedChilds) {
      reloadAllChilds();
    }
  }, [reloadSelected, reloadSelectedChilds]);

  useEffect(() => {
    if (records && records.length > 0) {
      if (orgStructure.autoloadParents) {
        toggleParents(orgStructure.id);
        orgStructure.autoloadParents = false;
      }
    }
  }, [records]);

  const zoomInOut = (mouseX, mouseY, zoomIn) => {
    const stage = stageRef.current;
    const scaleBy = 1.1;
    const oldScale = stage.scaleX();
    const mousePointTo = {
      x: mouseX / oldScale - stage.x() / oldScale,
      y: mouseY / oldScale - stage.y() / oldScale,
    };

    const newScale = zoomIn ? oldScale * scaleBy : oldScale / scaleBy;

    setStageScale(newScale);
    setStagePosition({
      x: -(mousePointTo.x - mouseX / newScale) * newScale,
      y: -(mousePointTo.y - mouseY / newScale) * newScale,
    });
  };

  const handleZoomIn = () => {
    const stage = stageRef.current;
    const mouseX = stage.width() / 2;
    const mouseY = stage.height() / 2;
    zoomInOut(mouseX, mouseY, true);
  };

  const handleZoomOut = () => {
    const stage = stageRef.current;
    const mouseX = stage.width() / 2;
    const mouseY = stage.height() / 2;
    zoomInOut(mouseX, mouseY, false);
  };

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    zoomInOut(
      stage.getPointerPosition().x,
      stage.getPointerPosition().y,
      e.evt.deltaY < 0,
    );
  };

  const resetZoom = () => {
    stageRef.current.position({ x: 0, y: 0 });
    setStagePosition({ x: 0, y: 0 });
    setStageScale(1);
  };

  const findInOrgStructure = (orgStruct, id) => {
    const foundList = [];
    if (orgStruct.id === id) {
      foundList.push(orgStruct);
    }
    if (orgStruct.parents) {
      for (let r of orgStruct.parents) {
        foundList.push(...findInOrgStructure(r, id));
      }
    }
    return foundList;
  };

  const toggleParents = async (id) => {
    setSelectedRecordId(id);
    const r = records.find((r) => r.id === id);
    r.isLoading = true;
    setSelectedRecord(r);
    const recs = findInOrgStructure(orgStructure, id);
    if (
      recs.length > 0 &&
      recs[0].showParents &&
      recs[0].parents &&
      recs[0].parents.length > 0
    ) {
      recs.forEach((r) => {
        r.showParents = false;
      });
      setOrgStructure({ ...orgStructure });
    } else {
      const parents = await getParents(id);
      if (recs.length > 0) {
        recs.forEach((r) => {
          r.parents = parents;
          r.showParents = true;
        });
        setOrgStructure({ ...orgStructure });
      } else {
        const {
          id,
          attributes,
          scores = [],
          addresses,
          permissions,
        } = entity || {};
        setOrgStructure({
          id,
          attributes,
          scores,
          permissions,
          address: getEntityAddress(addresses),
          parents: parents,
          showParents: true,
        });
      }
    }
    r.isLoading = false;
  };

  const selectRecord = (id) => {
    const r = records.find((r) => r.id === id);
    setSelectedRecordId(id);
    setSelectedRecord(r);
  };

  const onMenuClick = (id, pos) => {
    hideRecordInfo();
    setSelectedRecordId(id);
    const r = records.find((r) => r.id === id);
    setSelectedRecord(r);
    setSelectedRecordChilds(
      records.filter(
        (cr) => Object.keys(r.connectorPoints || {}).indexOf(cr.id) > -1,
      ),
    );
    const stage = stageRef.current;
    var containerRect = stage.container().getBoundingClientRect();
    const scrollLeft = stage.container().scrollLeft;
    let left = containerRect.left + pos.x - scrollLeft;
    if (left + 190 > window.innerWidth) {
      left = left - 160;
    } else {
      left = left + 30;
    }

    const scrollTop = stage.container().scrollTop + window.scrollY;
    const top = containerRect.top + pos.y + scrollTop; // + 30;//
    showRecordMenu({
      left,
      top,
    });
  };

  const onRecordInfoClick = (id, pos) => {
    hideRecordMenu();
    setSelectedRecordId(id);
    const r = records.find((r) => r.id === id);
    setSelectedRecord(r);
    setSelectedRecordChilds(
      records.filter(
        (cr) => Object.keys(r.connectorPoints || {}).indexOf(cr.id) > -1,
      ),
    );
    const stage = stageRef.current;
    const containerRect = stage.container().getBoundingClientRect();
    const scrollLeft = stage.container().scrollLeft;
    let left = containerRect.left + pos.x - scrollLeft;
    if (left + 330 > window.innerWidth) {
      left = left - 300;
    } else {
      left = left + 30;
    }

    const scrollTop = stage.container().scrollTop;
    const bottom = containerRect.bottom - (containerRect.top + pos.y);

    showRecordInfo(
      {
        left,
        bottom,
      },
      r,
    );
  };

  const onConnectorLblDblClick = (connector) => {
    if (connector.sourceRec.permissions.canUpdateAssociation) {
      selectRecord(connector.sourceRec.id);
      const sourceConnectors = connectors.filter(
        (c) => c.sourceRec.id === connector.sourceRec.id,
      );
      const sum = sourceConnectors
        .map((c) => c.value)
        .reduce(function (a, b) {
          return a + b;
        }, 0);
      showPercentageDialog(connector, sum);
    }
  };

  const onStageClick = (e) => {
    if (e.target === stageRef.current) {
      hideRecordMenu();
      hideRecordInfo();
      setSelectedRecordId(null);
      setSelectedRecord(null);
      setSelectedRecordChilds(null);
    }
  };

  const onDragStart = (e) => {
    hideRecordMenu();
    hideRecordInfo();
  };

  const onDragEnd = (e) => {
    setStagePosition(stageRef.current.position());
  };

  const saveAsImage = () => {
    const dataURL = stageRef.current.toDataURL();
    const link = document.createElement("a");
    link.download = `Ownership_${entity.urn}.png`;
    link.href = dataURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const saveAsPdf = () => {
    const stage = stageRef.current;
    const pdf = new jsPDF("l", "px", [stage.width(), stage.height()]);
    pdf.addImage(
      stage.toDataURL({ pixelRatio: 2 }),
      0,
      0,
      stage.width(),
      stage.height(),
    );

    pdf.save(`Ownership_${entity.urn}.pdf`);
  };

  const reload = async () => {
    const {
      id,
      urn,
      attributes,
      scores = [],
      addresses,
      permissions,
    } = entity || {};
    setOrgStructure({
      id,
      urn,
      attributes,
      scores,
      permissions,
      address: getEntityAddress(addresses),
      parents: [],
      showParents: false,
      autoloadParents: true,
    });
  };

  const hideRecords = (percentage) => {
    records.forEach((r) => {
      const connectedFrom = connectors.filter((c) => c.targetRec.id === r.id);
      connectedFrom.forEach((c) => {
        const p = c.value ? c.value : 0;
        c.hide = p < percentage;
      });
      const vcs = connectedFrom.filter((c) => {
        const p = c.value ? c.value : 0;
        return p >= percentage;
      });
      r.hide = connectedFrom.length > 0 && vcs.length === 0;
    });
    setConnectors([...connectors]);
    setRecords([...records]);
  };

  const expandParents = async (orgStruct, expandedList = []) => {
    if (expandedList.indexOf(orgStruct.id) < 0) {
      if (!orgStruct.showParents) {
        orgStruct.parents = await getParents(orgStruct.id);
        orgStruct.showParents = true;
      }
      expandedList.push(orgStruct.id);
      if (orgStruct.parents) {
        for (let r of orgStruct.parents) {
          await expandParents(r, expandedList);
        }
      }
    } else {
      orgStruct.showParents = true;
    }
  };

  const expandAll = async () => {
    records.forEach((r) => {
      if (!r.showParents) {
        r.isLoading = true;
      }
    });
    setRecords([...records]);
    await expandParents(orgStructure);
    setOrgStructure({ ...orgStructure });
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Toolbar
          entity={entity}
          stageRef={stageRef}
          handleZoomIn={handleZoomIn}
          handleZoomOut={handleZoomOut}
          resetZoom={resetZoom}
          reload={reload}
          saveAsImage={saveAsImage}
          saveAsPdf={saveAsPdf}
          hideRecords={hideRecords}
          expandAll={expandAll}
        />
      </Grid>
      {!hasAml && (
        <Grid item container xs={12} justify="center">
          <Typography>
            {t("messages.record.ownershipStructure.noAmlMessage", {
              relatedEntities: t("labels.record.sections.relatedEntities"),
            })}
          </Typography>
          {canCreateAml && (
            <Typography>
              {t("messages.record.ownershipStructure.startAmlMessage")}
            </Typography>
          )}
        </Grid>
      )}
      <Grid item xs={12}>
        <Stage
          container={"container"}
          width={canvasWidth}
          height={canvasHeight}
          draggable={true}
          scaleX={stageScale}
          scaleY={stageScale}
          x={stagePosition.x}
          y={stagePosition.y}
          //onWheel={handleWheel}
          onClick={onStageClick}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          ref={stageRef}
        >
          <Layer>
            {records
              .filter((r) => r.id !== selectedRecordId)
              .map((r) => (
                <Record
                  key={r.id}
                  entityId={entity.id}
                  record={r}
                  selected={r.id === selectedRecordId}
                  selectRecord={selectRecord}
                  toggleParents={toggleParents}
                  onMenuClick={onMenuClick}
                  hideMenu={hideRecordMenu}
                  onRecordInfoClick={onRecordInfoClick}
                  hideRecordInfo={hideRecordInfo}
                  hasRecordPermission={hasRecordPermission}
                />
              ))}
            {connectors
              .filter(
                (c) =>
                  c.sourceRec.id !== selectedRecordId &&
                  c.targetRec.id !== selectedRecordId,
              )
              .map((c) => (
                <Connector
                  key={c.targetRec.id + c.sourceRec.id}
                  connector={c}
                  selectedRecordId={selectedRecordId}
                  onLblDblClick={onConnectorLblDblClick}
                />
              ))}
            {records
              .filter((r) => r.id === selectedRecordId)
              .map((r) => (
                <Record
                  key={r.id}
                  record={r}
                  entityId={entity.id}
                  selected={r.id === selectedRecordId}
                  selectRecord={selectRecord}
                  toggleParents={toggleParents}
                  onMenuClick={onMenuClick}
                  hideMenu={hideRecordMenu}
                  onRecordInfoClick={onRecordInfoClick}
                  hideRecordInfo={hideRecordInfo}
                  hasRecordPermission={hasRecordPermission}
                />
              ))}
            {connectors
              .filter(
                (c) =>
                  c.sourceRec.id === selectedRecordId ||
                  c.targetRec.id === selectedRecordId,
              )
              .map((c) => (
                <Connector
                  key={c.targetRec.id + c.sourceRec.id}
                  connector={c}
                  selectedRecordId={selectedRecordId}
                  onLblDblClick={onConnectorLblDblClick}
                />
              ))}
          </Layer>
        </Stage>
      </Grid>
    </Grid>
  );
};

export default Canvas;
